import React, { useEffect, useState } from 'react'
import Switch from '@mui/material/Switch'
import Tooltip from '@mui/material/Tooltip'
import CustomButton from 'components/Button/CustomButton'
import useExchangeRate from 'hooks/useExchangeRate'
import { useSnackbar } from 'hooks/useSnackbar'
import metamaskIcon from '/assets/Icon/MetaMask.svg'
import magicIcon from '/assets/Icon/Logo-Magic.svg'
import crossmintIcon from '/assets/Icon/Crossmint.png'
import { useWallets } from 'providers/WalletsProvider'
import SetupWalletModal from 'components/Modal/SetupWalletModal'
import SpinnerModal from 'components/Modal/SpinnerModal'
import PaymentModal from 'components/Modal/PaymentModal'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import TopupCoachModal from 'components/Modal/TopupCoachModal'

interface WalletCardProps {
  walletName: 'Magic' | 'MetaMask' | 'Crossmint'
  showForSaleBreakdown?: boolean
  showEVM?: boolean
}

function WalletCard({
  walletName,
  showForSaleBreakdown = false,
  showEVM = false
}: WalletCardProps) {
  const { openSnackbar } = useSnackbar()
  const { rate } = useExchangeRate()
  const { isBanxaTopUpEnabled, isMoonpayTopUpEnabled } = useFeatureFlags()
  const {
    hasMagic,
    hasMetaMask,
    hasCrossmint,
    magicWalletAddress,
    metamaskWalletAddress,
    crossmintWalletAddress,
    connectedWalletAddress,
    magicWalletWUSDCBalance,
    metamaskWalletBalance,
    magicMomentCount,
    metamaskMomentCount,
    isConnected,
    connectMagic,
    connectMetaMask,
    connectCrossmint,
    disconnect,
    usingMagic,
    usingMetaMask,
    usingCrossmint,
    magicForSaleMomentCount,
    metamaskForSaleMomentCount,
    magicNotForSaleMomentCount,
    metamaskNotForSaleMomentCount,
    crossmintMomentCount,
    crossmintForSaleMomentCount,
    crossmintNotForSaleMomentCount,
    crossmintWalletBalance,
    showMagicWalletUI,
    exportMagicWalletPK,
    isExporting,
    exportError
  } = useWallets()
  const [spinner, setSpinner] = useState(false)
  const [setupWalletModalOpen, setSetupWalletModalOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [copiedAddress, setCopiedAddress] = useState(false)
  const [showCoach, setShowCoach] = useState(false)

  const isMagic = walletName === 'Magic'
  const isMetaMask = walletName === 'MetaMask'
  const isCrossmint = walletName === 'Crossmint'

  const hasAddedWallet = isMagic
    ? hasMagic
    : isCrossmint
    ? hasCrossmint
    : hasMetaMask
  const walletIcon = isMagic
    ? magicIcon
    : isCrossmint
    ? crossmintIcon
    : metamaskIcon
  const totalCollectiblesInAccount = isMagic
    ? magicMomentCount
    : isCrossmint
    ? crossmintMomentCount
    : metamaskMomentCount
  const forSaleCollectiblesInAccount = isMagic
    ? magicForSaleMomentCount
    : isCrossmint
    ? crossmintForSaleMomentCount
    : metamaskForSaleMomentCount
  const notForSaleCollectiblesInAccount = isMagic
    ? magicNotForSaleMomentCount
    : isCrossmint
    ? crossmintNotForSaleMomentCount
    : metamaskNotForSaleMomentCount
  const walletAddress = isMagic
    ? magicWalletAddress
    : isCrossmint
    ? crossmintWalletAddress
    : metamaskWalletAddress
  const _isConnected = isMagic
    ? usingMagic && isConnected
    : isCrossmint
    ? usingCrossmint && isConnected
    : usingMetaMask && isConnected
  const walletBalance = isMagic
    ? magicWalletWUSDCBalance
    : isCrossmint
    ? crossmintWalletBalance
    : metamaskWalletBalance

  const userBalanceAUD = Number(walletBalance * rate).toFixed(2)
  const USDCBalance = Number(walletBalance).toFixed(2)

  const handleClose = () => {
    setAnchorEl(null)
  }

  // Crossmint is the only wallet a user can connect on Base for now.
  //
  // MetaMask stays visible but inert ("Coming soon") — connecting it sets
  // lastUsedWallet='metamask', and several flows still read that to decide
  // which wallet to act on. It gets enabled once the Crossmint path is proven
  // and key export exists, so a user can import their wallet into MetaMask
  // rather than end up with two unrelated ones.
  //
  // Magic was the Flow-era wallet. Flow is retired, so it cannot connect at
  // all; its card is already commented out of ManageWallets.
  const METAMASK_ENABLED = false

  const handleConnectClick = () => {
    if (isMetaMask) {
      if (!METAMASK_ENABLED) {
        openSnackbar('MetaMask support is coming soon — use Crossmint for now.')
        return
      }
      handleMetaMaskConnect()
      return
    }

    if (isCrossmint) {
      handleCrossmintConnect()
      return
    }

    if (isMagic) {
      openSnackbar('Magic wallets are no longer supported. Use Crossmint.')
      return
    }

    if (!_isConnected && !hasAddedWallet) {
      setSetupWalletModalOpen(true)
      return
    }

    handleClose()
  }

  const handleMagicConnect = () => {
    if (!_isConnected) {
      connectMagic()
      setSpinner(true)
    } else {
      disconnect()
    }
  }

  const handleMetaMaskConnect = async () => {
    if (!_isConnected) {
      const result = await connectMetaMask()
      if (!result?.success) {
        if (result?.error === 'not_installed') {
          openSnackbar(
            'MetaMask is not installed. Redirecting to install page...'
          )
          window.open(
            'https://chromewebstore.google.com/detail/metamask/nkbihfbeogaeaoehlefnkodbefgpgknn',
            '_blank'
          )
        } else if (result?.error === 'no_accounts') {
          openSnackbar(
            'No accounts found. Please create an account in MetaMask first.'
          )
        } else {
          openSnackbar(`MetaMask connection failed: ${result?.error}`)
        }
      }
    } else {
      disconnect()
    }
  }

  const handleCrossmintConnect = async () => {
    if (!_isConnected) {
      const result = await connectCrossmint()
      if (!result?.success) {
        openSnackbar(`Crossmint wallet error: ${result?.error}`)
      }
    } else {
      disconnect()
    }
  }

  const copyWalletAddress = () => {
    navigator.clipboard.writeText(walletAddress)
    setCopiedAddress(true)
    setTimeout(() => setCopiedAddress(false), 2000)
    openSnackbar(
      isMagic
        ? 'Flow address copied to clipboard'
        : 'Wallet address copied to clipboard'
    )
  }

  const handleOpenPaymentModal = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget)
  }

  useEffect(() => {
    if (isConnected || connectedWalletAddress === magicWalletAddress) {
      setSpinner(false)
    }
  }, [connectedWalletAddress, magicWalletAddress, isConnected])

  const showAddFundsButton =
    _isConnected && isMagic && (isBanxaTopUpEnabled || isMoonpayTopUpEnabled)

  const truncateAddress = (address: string) => {
    if (!address) return '...'
    return `${address.slice(0, 6)}...${address.slice(-4)}`
  }

  const addressLabel = isMagic ? 'Flow Address' : 'Wallet Address'
  const addressTooltip = isMagic
    ? 'Your Flow address is your main address for the Flow blockchain.'
    : isCrossmint
    ? 'Your Crossmint custodial wallet address on the Base network.'
    : 'Your MetaMask wallet address on the Base network.'

  const descriptionText = isMagic
    ? 'Magic is a new wallet option that reduces log in and purchase steps for a more seamless experience.'
    : isCrossmint
    ? 'Crossmint provides a seedless custodial wallet with card payment support on the Base network.'
    : 'MetaMask is a popular Ethereum wallet for managing your digital assets on the Base network.'

  return (
    <div className="flex w-full flex-col justify-between gap-2 rounded-[3px] bg-card p-4">
      <div className="flex w-full flex-col gap-2">
        <div className="flex w-full items-center justify-between font-bold">
          <div className="flex items-center gap-2">
            <img
              src={walletIcon}
              alt={`Logo ${walletName}`}
              className="h-6 w-[24px]"
            />
            <div className="text-base text-white">{walletName}</div>
          </div>
          <Switch
            sx={{
              padding: '6px',
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#ffffff'
              },
              '& .MuiSwitch-track': {
                borderRadius: '16px',
                backgroundColor: '#7E7E7E'
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#48a0ff'
              }
            }}
            checked={_isConnected}
            onChange={handleConnectClick}
          />
        </div>

        <div className="mt-2 flex w-full gap-2">
          <div className="flex-1 rounded bg-[#2a2a2a] p-2">
            <div className="mb-1 flex items-center justify-between">
              <Tooltip title={addressTooltip}>
                <span className="text-xs font-semibold text-[#a6a6a6]">
                  {addressLabel}
                </span>
              </Tooltip>
              <button
                className={`rounded px-2 py-1 text-xs ${
                  copiedAddress
                    ? 'bg-green-500 text-white'
                    : 'bg-[#3a3a3a] text-[#a6a6a6] hover:bg-[#4a4a4a]'
                }`}
                onClick={copyWalletAddress}
              >
                {copiedAddress ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="break-all text-sm text-white">
              {walletAddress ? truncateAddress(walletAddress) : '...'}
            </div>
          </div>
        </div>

        {hasAddedWallet === false && (
          <div className="flex w-full flex-col items-start gap-2 border-t border-[#3e3e3e] py-2">
            <div className="flex w-full items-start justify-between gap-2 text-[#d3d3d3]">
              {descriptionText}
            </div>
          </div>
        )}
        {hasAddedWallet && (
          <div className="flex w-full flex-col items-start gap-2 border-t border-[#3e3e3e] py-2">
            <div className="flex w-full items-start justify-between gap-2">
              <div className="flex flex-col items-start">
                {showForSaleBreakdown && (
                  <>
                    <div className="text-sm font-bold">
                      Collectibles (not for sale)
                    </div>
                    <div className="text-sm font-bold">
                      Collectibles (for sale)
                    </div>
                  </>
                )}
                {!showForSaleBreakdown && (
                  <div className="text-sm font-bold">Collectibles</div>
                )}
              </div>
              <div className="flex flex-col items-end text-right text-sm">
                {showForSaleBreakdown && (
                  <>
                    <div className="font-bold">
                      {notForSaleCollectiblesInAccount}
                    </div>
                    <div className="font-bold">
                      {forSaleCollectiblesInAccount}
                    </div>
                  </>
                )}
                {!showForSaleBreakdown && (
                  <div className="font-bold">{totalCollectiblesInAccount}</div>
                )}
              </div>
            </div>
            <div className="relative flex w-full items-start justify-between gap-2">
              <div className="flex flex-col items-start">
                <div className="text-sm font-bold">Balance</div>
              </div>
              <div className="flex flex-col items-end text-right text-sm">
                <div className="font-bold">A${userBalanceAUD}</div>
                <div className="text-grey2">({USDCBalance} USDC)</div>
              </div>
            </div>
          </div>
        )}
      </div>
      {_isConnected ? (
        <>
          {showAddFundsButton && (
            <CustomButton
              label="Add funds"
              model="primary"
              variant="contained"
              onClick={() => setShowCoach(true)}
            />
          )}
        </>
      ) : (
        <CustomButton
          label={
            isMetaMask && !METAMASK_ENABLED
              ? 'Coming soon'
              : isMagic
              ? 'No longer supported'
              : hasAddedWallet
              ? 'Connect Wallet'
              : 'Get Started'
          }
          model="secondary"
          variant="contained"
          disabled={(isMetaMask && !METAMASK_ENABLED) || isMagic}
          onClick={handleConnectClick}
        />
      )}
      {_isConnected && isMagic && (
        <>
          <CustomButton
            label="Manage Funds"
            model="secondary"
            variant="outlined"
            onClick={() => showMagicWalletUI()}
          />
          <div className="mt-3 text-center">
            <button
              onClick={() => exportMagicWalletPK()}
              className={`mt-2 block text-sm text-blue-500 hover:text-blue-400 hover:underline ${
                isExporting ? 'cursor-not-allowed opacity-50' : ''
              }`}
              disabled={isExporting}
            >
              {isExporting ? 'Exporting...' : 'Export Private Key'}
            </button>
            <p className="mt-1 px-2 text-xs text-red-500">
              Warning: Exporting your private key gives full control over your
              wallet. Store it securely offline and NEVER share it with anyone.
            </p>
            {exportError && (
              <p className="mt-1 px-2 text-xs text-red-500">{exportError}</p>
            )}
          </div>
        </>
      )}
      {/* Commented out - Blocto "Manage Funds (Get App)" button removed */}
      {/* {_isConnected && !isMagic && (
        <a href="https://blocto.io/download" target="_blank" rel="noreferrer">
          <CustomButton
            label="Manage Funds (Get App)"
            model="secondary"
            variant="outlined"
            isExternalLink={true}
            className="w-full"
          />
        </a>
      )} */}

      <SetupWalletModal
        isMagic={isMagic}
        setupWalletModalOpen={setupWalletModalOpen}
        setSetupWalletModalOpen={setSetupWalletModalOpen}
      />
      <SpinnerModal
        spinnerModalOpen={spinner}
        setSpinnerModalOpen={setSpinner}
      />
      <PaymentModal
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        updateActiveStep={2}
        checkoutType="funds"
      />
      <TopupCoachModal
        TopupCoachModalOpen={showCoach}
        onClose={() => {
          setShowCoach(false)
        }}
        onContinue={() => {
          handleOpenPaymentModal({
            currentTarget: document.createElement('button')
          } as React.MouseEvent<HTMLButtonElement>)
        }}
      />
    </div>
  )
}
export default WalletCard
